﻿import { useEffect, forwardRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText,
  Box,
} from '@mui/material';

/**
 * A Material-UI Select component integrated with React Query for dynamic data fetching.
 * Automatically handles loading states, errors, and empty data scenarios.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string|Array} props.queryKey - React Query key for caching and refetching
 * @param {Function} props.queryFn - React Query function that fetches the data
 * @param {Object} [props.queryOptions={}] - Additional React Query options (select, enabled, etc.)
 * @param {string} props.label - Label text for the select input
 * @param {*} props.value - Current selected value
 * @param {Function} props.onChange - Callback fired when selection changes. Receives event object with target.value
 * @param {number|string} [props.minWidth] - Minimum width for the FormControl
 * @param {Function} [props.getOptionLabel] - Function to extract display label from option object. Defaults to option.name ?? option.text ?? option.label ?? String(option)
 * @param {Function} [props.getOptionValue] - Function to extract value from option object. Defaults to option.id ?? option.value ?? option
 * @param {string} [props.placeholder="Select an option"] - Placeholder text shown as disabled first option
 * @param {string} [props.emptyText="No options available"] - Helper text shown when data loads but contains no options
 * @param {boolean} [props.defaultToFirst=false] - If true, automatically selects first option when data loads and no value is set
 * @param {Function} [props.onDataLoaded] - Callback fired when data successfully loads. Receives (options[], rawData)
 * @param {boolean} [props.error=false] - If true, displays the select in error state (typically from form validation)
 * @param {string} [props.helperText] - Helper text displayed below the select. Overrides emptyText if provided
 * @param {boolean} [props.disabled=false] - If true, disables the select input
 * @param {Array} [props.fallbackData=[]] - Fallback data to use when query returns empty results
 * @param {Object} props...props - Additional props spread to FormControl component
 *
 * @example
 * // Basic usage
 * <QuerySelect
 *   queryKey={['users']}
 *   queryFn={fetchUsers}
 *   label="Select User"
 *   value={selectedUser}
 *   onChange={(e) => setSelectedUser(e.target.value)}
 * />
 *
 * @example
 * // With custom option formatting and auto-select
 * <QuerySelect
 *   queryKey={['agencies', producerId]}
 *   queryFn={() => getAgencies(producerId)}
 *   label="Agency"
 *   value={agency}
 *   onChange={(e) => setAgency(e.target.value)}
 *   getOptionLabel={(option) => option.agencyName}
 *   getOptionValue={(option) => option.agencyId}
 *   defaultToFirst={true}
 *   emptyText="No agencies found"
 * />
 *
 * @example
 * // With fallback data
 * <QuerySelect
 *   queryKey={['agencies']}
 *   queryFn={getAgencies}
 *   label="Agency"
 *   value={agency}
 *   onChange={(e) => setAgency(e.target.value)}
 *   fallbackData={[
 *     { id: 'default-1', name: 'Default Agency' }
 *   ]}
 * />
 *
 * @example
 * // With React Hook Form Controller
 * <Controller
 *   name="policyId"
 *   control={control}
 *   render={({ field, fieldState: { error } }) => (
 *     <QuerySelect
 *       {...field}
 *       queryKey={['policies']}
 *       queryFn={getPolicies}
 *       label="Policy"
 *       error={!!error}
 *       helperText={error?.message}
 *     />
 *   )}
 * />
 */
const QuerySelect = forwardRef(function QuerySelect(
  {
    queryKey,
    queryFn,
    queryOptions = {},
    label,
    value,
    onChange,
    minWidth,
    getOptionLabel = (option) => option.name ?? option.text ?? option.label ?? String(option),
    getOptionValue = (option) => option.id ?? option.value ?? option,
    placeholder = 'Select an option',
    emptyText = 'No options available',
    defaultToFirst = false,
    onDataLoaded,
    error: formError = false,
    helperText,
    disabled = false,
    fallbackData = [],
    ...props
  },
  ref
) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey,
    queryFn,
    ...queryOptions,
  });

  // Normalize data structure - handles both array and object with data property
  const options = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];

  // Use fallback data if query returned empty OR if query errored and fallback is available
  const finalOptions =
    (options.length === 0 || isError) && fallbackData.length > 0 ? fallbackData : options;

  /**
   * Effect to automatically select the first option when data loads
   * Only triggers if defaultToFirst is true, data exists, and no value is currently set
   */
  useEffect(() => {
    if (defaultToFirst && finalOptions.length > 0 && !value && onChange) {
      const firstValue = getOptionValue(finalOptions[0]);
      onChange({ target: { value: firstValue } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalOptions, defaultToFirst, value]);

  /**
   * Effect to notify parent component when data loads
   * Normalizes the data structure to always return an array
   */
  useEffect(() => {
    if (data && onDataLoaded) {
      onDataLoaded(finalOptions, data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Loading state - shows disabled select with spinner
  if (isLoading) {
    return (
      <FormControl fullWidth sx={{ minWidth: minWidth || '' }} {...props}>
        <InputLabel>{label}</InputLabel>
        <Select
          value=""
          label={label}
          disabled
          IconComponent={() => (
            <Box style={{ display: 'flex', alignItems: 'center', marginRight: 8 }}>
              <CircularProgress size={20} />
            </Box>
          )}
        >
          <MenuItem value="">Loading...</MenuItem>
        </Select>
      </FormControl>
    );
  }

  // Error state - use fallback data if available, otherwise show error
  if (isError && fallbackData.length === 0) {
    return (
      <FormControl fullWidth sx={{ minWidth: minWidth || '' }} error {...props}>
        <InputLabel>{label}</InputLabel>
        <Select value="" label={label} disabled>
          <MenuItem value="">Error loading options</MenuItem>
        </Select>
        <FormHelperText>{error?.message || 'Failed to load options'}</FormHelperText>
      </FormControl>
    );
  }

  const showEmptyText = data && finalOptions.length === 0;
  const displayHelperText = helperText || (showEmptyText ? emptyText : null);

  return (
    <FormControl sx={{ minWidth: minWidth || '' }} fullWidth error={formError} {...props}>
      <InputLabel>{label}</InputLabel>
      <Select
        inputRef={ref}
        value={value || ''}
        onChange={onChange}
        label={label}
        disabled={disabled}
      >
        {placeholder && (
          <MenuItem value="" disabled>
            {placeholder}
          </MenuItem>
        )}
        {finalOptions != null &&
          finalOptions.map((option) => (
            <MenuItem key={getOptionValue(option)} value={getOptionValue(option)}>
              {getOptionLabel(option)}
            </MenuItem>
          ))}
      </Select>
      {displayHelperText && <FormHelperText>{displayHelperText}</FormHelperText>}
    </FormControl>
  );
});

export default QuerySelect;
